import {
  MessagesData,
  fetchConversationMessages,
  fetchMessages,
} from '@nextforce/api'
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'

import { useServer } from 'app/server/context'
import { useChatConfig } from 'chat/config/queries/get'
import { useMessagingContext } from 'chat/messaging_context'
import keys from 'chat/shared/queries/keys'
import { useCallback, useEffect } from 'react'
import { RetryQuery } from 'shared/queries/utils'
import { useUser } from 'users/queries/detail'
import { updateConversationRecentMessages } from './shared/utils'

type ConversationPaginationState = {
  hasMore: boolean | undefined
}

// Configurable time window for fetching messages (in days)
// Set to a smaller value (e.g., 1) for testing
export const INITIAL_MESSAGE_FETCH_WINDOW_DAYS = 28 // Fetch last N days of messages during initial fetch
export const LOAD_EARLIER_LIMIT = 100 // Load N messages at a time when loading earlier messages

export function useNonDeletedMessages<T = MessagesData>(
  select: (messages: MessagesData) => T,
  retryQuery?: RetryQuery
) {
  return useMessages(data => {
    const filteredMessages = {
      ...data,
      messages: data.messages.filter(message => !message.isDeleted),
    }
    return select(filteredMessages)
  }, retryQuery)
}

export function useMessages<T = MessagesData>(
  select: (messages: MessagesData) => T,
  retryQuery?: RetryQuery
) {
  const { server } = useServer()
  const client = useQueryClient()
  const { data: chatConfig } = useChatConfig()
  const { data: user } = useUser()
  const { setProgress } = useMessagingContext()

  const query = useQuery({
    queryKey: keys.messages(),
    queryFn: fetchMessagesData,
    retry: retryQuery ?? false,
    refetchOnMount: 'always',
    select,
    enabled: user?.permissions?.canSeeMessages,
  })

  // Replaces onSuccess
  useEffect(() => {
    if (query.isSuccess) {
      updateConversationRecentMessages(client).catch(e => null)
    }
  }, [query.isSuccess, client])

  return query

  async function fetchMessagesData(): Promise<MessagesData> {
    const cachedData = client.getQueryData<MessagesData>(keys.messages())

    let newestEventId: string | undefined
    let oldestEventId: string | undefined
    let messages: any[] = []

    if (cachedData) {
      // Incremental fetch: only get NEW messages after the newest cached message
      // Don't pass oldestEventId to API to avoid creating a bounded range of messages we already have
      messages = cachedData.messages
      newestEventId = cachedData.newestEventId
      // Don't pass oldestEventId to API for incremental fetch
      oldestEventId = undefined
    } else {
      // Initial fetch: only fetch last N days
      // Use start_after to get messages AFTER (newer than) N days ago
      const defaultFetchAfterDate = new Date()
      defaultFetchAfterDate.setDate(
        defaultFetchAfterDate.getDate() - INITIAL_MESSAGE_FETCH_WINDOW_DAYS
      )
      const timestampStr = defaultFetchAfterDate.getTime().toString()

      // Set newestEventId to fetch messages after this date
      // Set oldestEventId to same value initially to establish the baseline
      newestEventId = timestampStr
      oldestEventId = undefined
    }

    const result = await fetchMessages(
      server,
      {
        newestEventId,
        oldestEventId,
        // Only apply maxMessageAge on initial fetch, not incremental fetch
        // to prevent removing cached messages during merge
        maxMessageAge: undefined,
      },
      messages,
      setProgress
    )

    // For incremental fetch, the API returns oldestEventId based on the NEW messages only,
    // but we need to preserve the cached oldestEventId which points to the actual oldest message
    if (cachedData && cachedData.oldestEventId) {
      result.oldestEventId = cachedData.oldestEventId
    }

    return result
  }
}

export function useLoadEarlierConversationMessages(conversationId: string) {
  const { server } = useServer()
  const client = useQueryClient()

  // Track pagination state per conversation using React Query
  const { data: paginationState } = useQuery<ConversationPaginationState>({
    queryKey: keys.conversationPagination(conversationId),
    queryFn: () => ({ hasMore: undefined }), // Default to undefined for new conversations
    staleTime: Infinity, // Never refetch, only update manually
    gcTime: Infinity, // Keep in cache forever
  })

  const setHasMore = useCallback(
    (hasMore: boolean | undefined) => {
      client.setQueryData<ConversationPaginationState>(
        keys.conversationPagination(conversationId),
        { hasMore }
      )
    },
    [client, conversationId]
  )

  const loadEarlier = useCallback(async (): Promise<void> => {
    const queryKey = keys.messages()
    const cachedData = client.getQueryData<MessagesData>(queryKey)

    if (!cachedData) {
      setHasMore(false)
      return
    }

    // Find the oldest message for this conversation
    const conversationMessages = cachedData.messages
      .filter(m => m.conversationId === conversationId)
      .sort((a, b) => a.createdAt - b.createdAt)

    if (conversationMessages.length === 0) {
      // Fetch initial messages for this conversation
      const result = await fetchConversationMessages(
        server,
        {
          conversationId,
          limit: LOAD_EARLIER_LIMIT,
        },
        cachedData.messages
      )

      // Merge with existing cache, preserving boundaries
      const updatedResult: MessagesData = {
        ...cachedData,
        messages: result.messages,
      }

      client.setQueryData(queryKey, updatedResult)
      setHasMore(result.hasMore)
      return
    }

    const oldestMessage = conversationMessages[0]
    const oldestEventId = oldestMessage.id

    // Fetch previous 100 messages before the oldest message
    const result = await fetchConversationMessages(
      server,
      {
        conversationId,
        startBefore: oldestEventId,
        limit: LOAD_EARLIER_LIMIT,
      },
      cachedData.messages
    )

    // When loading earlier messages, preserve the cached newestEventId
    // (should point to today's newest message) and only update oldestEventId for this conversation
    const updatedResult: MessagesData = {
      messages: result.messages,
      newestEventId: cachedData.newestEventId, // Preserve the newest boundary
      oldestEventId: cachedData.oldestEventId, // Preserve the global oldest boundary
    }

    // Update the cache with merged messages
    client.setQueryData(queryKey, updatedResult)

    // Update the pagination state for this conversation
    setHasMore(result.hasMore)
  }, [server, client, conversationId, setHasMore])

  return {
    loadEarlier,
    hasMore: paginationState?.hasMore,
  }
}

export function useLoadEarlierMessages() {
  const { server } = useServer()
  const client = useQueryClient()

  const loadEarlier = async (): Promise<void> => {
    const queryKey = keys.messages()
    const cachedData = client.getQueryData<MessagesData>(queryKey)

    if (!cachedData || !cachedData.oldestEventId) {
      return
    }

    // Calculate the date N days before the oldest message
    const oldestMessageDate = new Date(
      typeof cachedData.oldestEventId === 'string'
        ? parseInt(cachedData.oldestEventId.split('-')[0])
        : cachedData.oldestEventId
    )
    const fetchBeforeDate = new Date(oldestMessageDate)
    fetchBeforeDate.setDate(
      fetchBeforeDate.getDate() - INITIAL_MESSAGE_FETCH_WINDOW_DAYS
    )

    // Convert date to timestamp string for API compatibility
    const fetchStartDateTimestamp = fetchBeforeDate.getTime().toString()

    // Fetch messages in a BOUNDED RANGE using both startAfter and startBefore
    // This creates a time window: messages AFTER fetchBeforeDate AND BEFORE oldestEventId
    // Example: If oldest is 28 days ago, this fetches messages from 56 to 28 days ago
    const result = await fetchMessages(
      server,
      {
        newestEventId: fetchStartDateTimestamp, // start_after: lower bound (older date)
        oldestEventId: cachedData.oldestEventId, // start_before: upper bound (newer date)
        maxMessageAge: undefined,
      },
      cachedData.messages
    )

    const updatedResult: MessagesData = {
      ...result,
      oldestEventId: fetchStartDateTimestamp,
    }

    // Update the cache with merged messages
    client.setQueryData(queryKey, updatedResult)
  }

  return { loadEarlier }
}

export async function refetchMessagesIfNotExist(
  client: QueryClient,
  messageId: string
) {
  const queryKey = keys.messages()
  const messages = client.getQueryData<MessagesData>(queryKey)?.messages
  if (messages && messages.findIndex(message => message.id === messageId) < 0) {
    await client.invalidateQueries({ queryKey })
    await client.refetchQueries({ queryKey })
  }
  return
}
